import React, { useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { Redirect } from "react-router-dom";
import { StripeSubscriptionStatus, MembershipLabels, SCHMODEL_INSTAGRAM_DM_URL } from "@schmodel/core";
import "./index.css";
import logo from "../../assets/images/schmodelplain-withR-white.png";
import TermsOfMembership from "./TermsOfMembership";

const REGISTRATION = gql`
  query registration($token: String!) {
    registration(token: $token) {
      membership
      termsAccepted
      stripeSubscriptionStatus
    }
  }
`;

const ACCEPT_MEMBERSHIP_TERMS = gql`
  mutation acceptMembershipTerms($token: String!) {
    acceptMembershipTerms(token: $token) {
      checkoutUrl
    }
  }
`;

const Chevron = ({ open }) => (
    <svg className={`terms-chevron ${open ? "open" : ""}`} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

function MembershipTerms({ match }) {
    const token = match.params.token;
    const [termsOpen, setTermsOpen] = useState(false);
    const [checkoutUrl, setCheckoutUrl] = useState(null);

    const { data, loading, error } = useQuery(REGISTRATION, { variables: { token } });
    const [acceptTerms, { loading: accepting, error: acceptError }] = useMutation(ACCEPT_MEMBERSHIP_TERMS);

    const accept = async () => {
        const result = await acceptTerms({ variables: { token } });
        setCheckoutUrl(result?.data?.acceptMembershipTerms?.checkoutUrl || null);
    };

    if (loading) return <div className="payment-container" />;

    // Expired or tampered link.
    if (error) {
        return (
            <Shell>
                This link is no longer valid.
                <p className="payment-note">
                    Links stay active for 7 days. <a className="payment-link" href={SCHMODEL_INSTAGRAM_DM_URL}>Contact us</a> and
                    we will send a new one.
                </p>
            </Shell>
        );
    }

    const registration = data.registration;
    if (registration.stripeSubscriptionStatus === StripeSubscriptionStatus.ACTIVE) return <Redirect to="/membership/payment-success" />;

    // They have a subscription, but it owes money. Starting another one would bill them twice.
    if (registration.stripeSubscriptionStatus) {
        return (
            <Shell>
                There is a problem with your membership payment.
                <p className="payment-note">
                    Your last payment did not go through, so your membership is not active.
                    Please <a className="payment-link" href={SCHMODEL_INSTAGRAM_DM_URL}>contact us</a> and we
                    will help you sort it out.
                </p>
            </Shell>
        );
    }

    return (
        <Shell>
            {MembershipLabels[registration.membership] &&
                <p className="terms-membership">{MembershipLabels[registration.membership]}</p>}
            Please review and accept the terms of membership.

            <div className="terms-panel">
                <button type="button" className="terms-toggle" aria-expanded={termsOpen}
                        onClick={() => setTermsOpen(!termsOpen)}>
                    Terms of membership
                    <Chevron open={termsOpen} />
                </button>
                <div className={`terms-scroll ${termsOpen ? "open" : ""}`}>
                    <div className="terms-inner"><TermsOfMembership /></div>
                </div>
            </div>

            {checkoutUrl ? (
                <a className="payment-cta" href={checkoutUrl}>Continue to payment</a>
            ) : (
                <label className="terms-accept">
                    <input type="checkbox" disabled={accepting} onChange={accept} />
                    <span>I have read and accepted the terms of membership</span>
                </label>
            )}

            {acceptError && <p className="payment-note">Something went wrong. Please try again.</p>}
        </Shell>
    );
}

function Shell({ children }) {
    return (
        <div className="payment-container">
            <div className="payment-main payment-main-wide">{children}</div>
            <div className="payment-footer">
                <img src={logo} className="payment-logo" alt="Schmodel" />
            </div>
        </div>
    );
}

export default MembershipTerms;
